import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from '@mui/material';

/**
 * Firm-controlled automation settings.
 *
 * Court LOR auto-send used to be an environment variable, which meant only the developers could
 * change it. Lloyd asked for the switch himself (Jul 18 2026), so it lives in the database and is
 * flipped here by any manager holding "Can manage firm settings".
 *
 * The unusable-email count sits next to the switch deliberately. Auto-send is only safe once court
 * contact data is clean, and that number is the honest measure of how clean it currently is.
 */

interface AutomationSettings {
  courtLorAutoSend: boolean;
  appearanceAutoNotify: boolean;
  canManage: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  courtsWithUnusableEmail: number;
  courtsTotal: number;
}

const ENDPOINT = (process.env.API_HOST || '') + '/api/v1/manager/settings/automation';

const AutomationSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<AutomationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(ENDPOINT, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to load automation settings');
      }
      setSettings((await response.json()) as AutomationSettings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load automation settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleToggle = async (
    key: 'courtLorAutoSend' | 'appearanceAutoNotify',
    nextValue: boolean,
  ) => {
    if (!settings) return;

    // Optimistic, but reverted on failure — a switch that lies about whether mail is going out
    // is worse than one that is briefly slow.
    const previous = settings[key];
    setSettings({ ...settings, [key]: nextValue });
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: nextValue }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to update automation settings');
      }
      await load();
    } catch (err) {
      setSettings({ ...settings, [key]: previous });
      setError(err instanceof Error ? err.message : 'Failed to update automation settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Automation
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !settings ? (
        <CircularProgress size={24} />
      ) : settings ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.courtLorAutoSend}
                onChange={(e) => void handleToggle('courtLorAutoSend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send letters of representation to courts automatically on approval"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.courtLorAutoSend
              ? 'Approved LORs are sent straight to courts that have a usable email address. Courts without one still route to the internal LOR mailbox.'
              : 'Approved LORs are saved as Outlook drafts for staff to review and send by hand.'}
          </Typography>

          {settings.courtsWithUnusableEmail > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {settings.courtsWithUnusableEmail} of {settings.courtsTotal} courts have a missing or
              placeholder email address. Their letters route to the internal LOR mailbox rather than
              to the court.
              <br />
              Note that this check cannot detect an address that is well-formed but simply wrong —
              those will be sent while auto-send is on.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Lloyd, Aug 10: a notice saying the client must appear should mark the case and email
              them. Shipped off — the detection is a model reading a scan, and the output tells
              somebody to attend court. Off still records and still shows; it just does not send. */}
          <FormControlLabel
            control={
              <Switch
                checked={settings.appearanceAutoNotify}
                onChange={(e) => void handleToggle('appearanceAutoNotify', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Email clients automatically when a scanned notice says they must attend court"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.appearanceAutoNotify
              ? 'When a pre-trial or other notice clearly states the client must attend, the case is marked and the client is emailed and texted without anyone reviewing it first.'
              : 'Notices are still read and the case is still marked, but nobody is emailed until a staff member confirms it on the case. Staff ticking it by hand always emails the client, whichever way this is set.'}
          </Typography>

          {!settings.appearanceAutoNotify && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Recommended while this is new. Watch what the notices are being read as for a week or
              two on the case screens, then turn this on once you are satisfied.
            </Alert>
          )}

          {!settings.canManage && (
            <Typography variant="body2" color="text.secondary">
              You do not have permission to change this. Ask a manager with settings access.
            </Typography>
          )}

          {settings.updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Last changed{settings.updatedBy ? ` by ${settings.updatedBy}` : ''} on{' '}
              {new Date(settings.updatedAt).toLocaleString()}
            </Typography>
          )}
        </Box>
      ) : null}
    </Paper>
  );
};

export default AutomationSettingsPanel;
